import React from "react";
import { Check, Copy } from "lucide-react";
import * as Sentry from "@sentry/react";
import { StreamMessage, WebSocketMessage } from "@/views/chat/types";

interface CopyConversationProps {
  messages: StreamMessage[];
  allEvents: WebSocketMessage[];
  isStreaming: boolean;
  streamingMessageId: string | null;
}

export const CopyConversation: React.FC<CopyConversationProps> = ({
  messages,
  streamingMessageId,
  isStreaming,
  allEvents,
}) => {
  const [copiedConversation, setCopiedConversation] = React.useState(false);
  const copyConversation = React.useCallback(async () => {
    let conversationText = "";
    messages.forEach(message => {
      const sender = message.isUser ? "User" : "Assistant";
      conversationText += `${sender}: ${message.content}\n\n`;
      if (!message.isUser && message.evidences.length > 0) {
        conversationText += "Evidences:\n";
        message.evidences.forEach(evidence => {
          conversationText += `Evidence ${evidence.citationId}: ${JSON.stringify(evidence)}\n`;
        });
        conversationText += "\n";
      }
    });
    if (isStreaming && streamingMessageId) {
      const streamingMessage = messages.find(msg => msg.id === streamingMessageId);
      if (streamingMessage) {
        conversationText += `Assistant (streaming): ${streamingMessage.content}\n\n`;
        if (streamingMessage.evidences.length > 0) {
          conversationText += "Evidences:\n";
          streamingMessage.evidences.forEach(evidence => {
            conversationText += `Evidence ${evidence.citationId}: ${JSON.stringify(evidence)}\n`;
          });
          conversationText += "\n";
        }
      }
    }
    conversationText += "All Events:\n";
    allEvents.forEach((event, idx) => {
      conversationText += `Event ${idx + 1} (${event.ev_type}): ${JSON.stringify(event)}\n`;
    });
    try {
      await navigator.clipboard.writeText(conversationText.trim());
      setCopiedConversation(true);
      setTimeout(() => setCopiedConversation(false), 2000);
    } catch (error) {
      Sentry.captureException(error);
    }
  }, [messages, isStreaming, allEvents, streamingMessageId]);

  return (
    <div className="mb-2 flex justify-end space-x-2">
      <button
        onClick={copyConversation}
        className="flex items-center rounded bg-white px-3 py-1 text-sm text-gray-500 transition-colors duration-200 hover:text-gray-700"
        title={copiedConversation ? "Copied!" : "Copy Entire Conversation"}
      >
        {copiedConversation ? <Check size={16} className="mr-1 text-green-500" /> : <Copy size={16} className="mr-1" />}
        {copiedConversation ? "Copied" : "Copy Conversation"}
      </button>
    </div>
  );
};
